/* eslint-disable react/no-danger */
import React from 'react';
import TetherComponent from 'react-tether';
import styles from './css/Dropdown.styl';
import Button from './Button';

/**
 * Dropdown module
 */
export default class Dropdown extends React.Component {
  static propTypes = {
    btnClassName: React.PropTypes.string,
    className: React.PropTypes.string,
    displayText: React.PropTypes.string,
    dropdownItems: React.PropTypes.array,
    id: React.PropTypes.string,
    isDropdownToggled: React.PropTypes.bool,
    tetherAttachment: React.PropTypes.string,
    tetherClasses: React.PropTypes.object,
    tetherOffset: React.PropTypes.string,
    tetherTargetAttachment: React.PropTypes.string,
    toggleDropdown: React.PropTypes.func.isRequired,
  };

  static defaultProps = {
    isDropdownToggled: false,
    tetherAttachment: 'top left',
    tetherClasses: {element: styles.dropdown},
    tetherOffset: '0 0',
    tetherTargetAttachment: 'bottom left',
  };

  /**
   * Dropdown Component Constructor
   * @param  {Object} props Passed in Properties
   * @return {Undefined} Returns nothing
   */
  constructor(props) {
    super(props);

    this.focusedIndex = -1;

    this.closeDropdown = this.closeDropdown.bind(this);
    this.handleClick = this.handleClick.bind(this);
    this.handleKeyDown = this.handleKeyDown.bind(this);
  }

  /**
   * Lifecycle Method
   * @return {Undefined} Returns nothing
   */
  componentDidMount() {
    document.addEventListener('click', this.closeDropdown);
    window.addEventListener('keydown', this.handleKeyDown);
  }

  /**
   * Lifecycle Method
   * @return {Undefined} Returns nothing
   */
  componentWillUnmount() {
    document.removeEventListener('click', this.closeDropdown);
    window.removeEventListener('keydown', this.handleKeyDown);
  }

  /**
   * Creates string containing the proper classes
   * @param  {String} classes Additional classes to be appended
   * @return {String} The formatted class string
   */
  getBtnClasses(classes) {
    const open = this.props.isDropdownToggled ? ` ${styles.open}` : '';
    const appended = classes ? ` ${classes}` : '';

    return `${styles.dropdownBtn}${open}${appended}`;
  }

  /**
   * Returns the chevron to display based on dropdown state
   * @param  {Boolean} isDropdownToggled If the dropdown is open or closed
   * @return {String} SVG source
   */
  getChevron() {
    return `<svg version="1.1" id="Layer_1" xmlns="https://www.w3.org/2000/svg"
        xmlns:xlink="https://www.w3.org/1999/xlink" x="0px" y="0px" viewBox="0 0 12.3 8.3"
        style="enable-background:new 0 0 12.3 8.3;" xml:space="preserve">
        <style type="text/css">
    	    .st3{fill:currentColor;}
        </style>
        <g>
    	    <polygon class="st3" points="6.2,7.8 0.5,2.7 2.5,0.5 6.2,3.8 9.8,0.5 11.8,2.7 	"/>
        </g>
      </svg>`;
  }

  /**
   * Formats the id if one is present in props
   * @param  {String} id The id string
   * @return {String} The constructed id
   */
  getId(id) {
    return id ? `${id}_dropdown` : '';
  }

  /**
   * Handler for when the dropdown button is clicked
   * @param  {Object} e The click event
   * @return {Undefined} Returns nothing
   */
  handleClick(e) {
    if (this.props.isDropdownToggled) {
      this.focusedIndex = -1;
    }
    this.props.toggleDropdown(e);
  }

  /**
   * Handler for tracking keyboard events related to dropdown actions
   * Escape closes the dropdown, and the arrow keys and tabbing navigates through the menu
   * @param  {Object} e The Event object
   * @return {Undefined} Returns nothing
   */
  handleKeyDown(e) {
    const code = e.keyCode ? e.keyCode : e.which;
    const ARROW_UP = 38;
    const ARROW_DOWN = 40;
    const ESC = 27;
    const TAB = 9;

    if (this.props.isDropdownToggled) {
      if (code === ESC) {
        this.focusedIndex = -1;
        this.refs.dropdown.getElementsByTagName('button')[0].focus();
        this.props.toggleDropdown(e);
      } else if (code === ARROW_UP || code === ARROW_DOWN) {
        e.preventDefault();
        const links = this.refs.dropdownList.getElementsByTagName('a');

        this.focusedIndex = this.calcFocusedIndex(
          this.focusedIndex,
          links.length,
          code === ARROW_UP
        );
        links[this.focusedIndex].focus();
      } else if (code === TAB) {
        e.preventDefault();
        const links = this.refs.dropdownList.getElementsByTagName('a');
        const length = links.length - 1;

        if (e.shiftKey) {
          this.focusedIndex--;
        } else {
          this.focusedIndex++;
        }
        if (this.focusedIndex > length || this.focusedIndex < 0) {
          this.focusedIndex = -1;
          this.refs.dropdown.getElementsByTagName('button')[0].focus();
          this.props.toggleDropdown(e);
        } else {
          links[this.focusedIndex].focus();
        }
      }
    }
  }

  /**
   * Calculates the index that should be focused on
   * @param  {Number}  currIndex The current focused index
   * @param  {Number}  length    The length of dropdown items
   * @param  {Boolean} isUp      If the direction of focus should be up or down
   * @return {Number} The new index
   */
  calcFocusedIndex(currIndex, length, isUp) {
    let newIndex = isUp ? currIndex - 1 : currIndex + 1;

    if (newIndex < 0) {
      newIndex = 0;
    } else if (newIndex > length - 1) {
      newIndex = length - 1;
    }

    return newIndex;
  }

  /**
   * Handler for closing the dropdown when outside the dropdown is clicked
   * @param  {Object} e The Event object
   * @return {Undefined} Returns nothing
   */
  closeDropdown(e) {
    if (this.props.isDropdownToggled) {
      const dropdown = this.refs.dropdown;

      if (!dropdown.contains(e.target)) {
        this.focusedIndex = -1;
        this.props.toggleDropdown(e);
      }
    }
  }

  /**
   * React Component render
   * @return {JSX} Modal rendered
   */
  render() {
    const {
      btnClassName,
      className,
      displayText,
      id,
      isDropdownToggled,
    } = this.props;
    const displayId = this.getId(id);

    return (
      <div
        className={`${styles.Dropdown}${className ? ` ${className}` : null}`}
        id={displayId}
        ref="dropdown"
      >
        <TetherComponent
          attachment={this.props.tetherAttachment}
          classes={this.props.tetherClasses}
          offset={this.props.tetherOffset}
          targetAttachment={this.props.tetherTargetAttachment}
        >
          <Button
            ariaExpanded={`${isDropdownToggled}`}
            ariaHaspopup="true"
            className={this.getBtnClasses(btnClassName)}
            handleClick={this.handleClick}
            id={displayId}
          >
            {displayText ? `${displayText} ` : ''}
            <span dangerouslySetInnerHTML={{__html: this.getChevron()}} />
          </Button>
          {isDropdownToggled ? (
            <ul aria-labelledby={`${displayId}_button`} ref="dropdownList">
              {this.props.dropdownItems.map((item, key) => (
                <li key={`dropdown-item-${key}`}>
                  <a {...item.props}>{item.displayText}</a>
                </li>
              ))}
            </ul>
          ) : null}
        </TetherComponent>
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./src/reactModules/shared/library/FormControls/Dropdown.js